import { useEffect, useRef, useState } from "react";

import { ApiError, apiFetch } from "./client";
import { useSupabase } from "@/auth/SupabaseProvider";

/**
 * Live-stream hook for a job's run events. Wraps the orchestrator's
 * SSE endpoint:
 *
 * 1. Fetch a short-lived ticket via `POST /api/jobs/:id/stream-ticket`.
 * 2. Open `EventSource(/api/jobs/:id/stream?ticket=...)`.
 * 3. Accumulate events into local state.
 * 4. On `terminal` event or `error`, close + (for error) refresh the
 *    ticket and reconnect once. A second consecutive failure surfaces
 *    as `connectionState: "errored"` so the UI can render a "stream
 *    disconnected" banner with a manual retry.
 *
 * Why not native EventSource auto-reconnect? It does work, but
 * EventSource cannot send headers — it would re-open with the same
 * (potentially expired) ticket from the URL. Manual reconnect lets us
 * mint a fresh ticket on each retry.
 *
 * The hook returns the latest snapshot (initial event from the server)
 * plus the running list of post-snapshot events. Components render the
 * combination — e.g. JobDetail merges the snapshot's audit/run logs with
 * the streamed deltas.
 *
 * `disabled: true` short-circuits the hook (no ticket fetch, no
 * EventSource). Used by JobDetail to skip the live stream once the job
 * reaches a terminal status (the existing 5-second polling carries the
 * page from there).
 */
export type ConnectionState = "idle" | "connecting" | "open" | "closed" | "errored";

export interface RunSnapshotPayload {
  status: string;
  agentType: string;
  context: unknown;
  startedAt: string | null;
  completedAt: string | null;
  runLog: Array<{
    agentType: string;
    callIndex: number;
    stopReason: string | null;
    startedAt: string;
    completedAt: string;
  }>;
  audit: Array<{
    eventType: string;
    actorType: string;
    actorId: string;
    payload: unknown;
    createdAt: string;
  }>;
}

export type RunStreamEvent =
  | { type: "state_transition"; jobId: string; from: string; to: string; reason?: string; at: string }
  | { type: "job_dispatched"; jobId: string; agentType: string; at: string }
  | { type: "dispatch_failed"; jobId: string; message: string; at: string }
  | {
      type: "callback_received";
      jobId: string;
      status: "success" | "failure";
      durationSeconds: number;
      errorCount: number;
      at: string;
    }
  | {
      type: "run_log_appended";
      jobId: string;
      agentType: string;
      callIndex: number;
      stopReason: string | null;
      startedAt: string;
      completedAt: string;
    }
  /**
   * Curator's `record_mode_progress` tool calls, forwarded by the
   * orchestrator's dispatch-runner from the agent's SSE stream. Drives
   * `ModeProgressPills` on JobDetail (issue
   * [#240](https://github.com/PROSPECT-TECH-LTD/prospect-platform/issues/240)).
   * `status` is the post-mapping two-state vocabulary
   * (`running`/`complete`); the agent's three-state input
   * (`started`/`completed`/`skipped`) is collapsed inside the curator
   * before emission.
   */
  | {
      type: "mode_progress";
      jobId: string;
      mode: string;
      status: "running" | "complete";
      at: string;
    }
  | { type: "terminal"; jobId: string; status: string; at: string };

export interface UseRunStreamResult {
  connectionState: ConnectionState;
  snapshot: RunSnapshotPayload | null;
  events: RunStreamEvent[];
  /** Final terminal status when the stream closes. Null while streaming. */
  terminalStatus: string | null;
}

const RECONNECT_ATTEMPTS = 1;

interface StreamTicketResponse {
  ticket: string;
  expiresAt: number;
  ttlSeconds: number;
}

const STREAM_BASE_URL: string = import.meta.env.VITE_API_BASE_URL ?? "";

export function useRunStream(
  jobId: string | undefined,
  options: { disabled?: boolean } = {},
): UseRunStreamResult {
  const { client } = useSupabase();
  const [connectionState, setConnectionState] = useState<ConnectionState>("idle");
  const [snapshot, setSnapshot] = useState<RunSnapshotPayload | null>(null);
  const [events, setEvents] = useState<RunStreamEvent[]>([]);
  const [terminalStatus, setTerminalStatus] = useState<string | null>(null);
  const sourceRef = useRef<EventSource | null>(null);

  useEffect(() => {
    if (!jobId || options.disabled) {
      setConnectionState("idle");
      return;
    }

    let cancelled = false;
    let attempts = 0;

    const open = async (): Promise<void> => {
      if (cancelled) return;
      setConnectionState("connecting");
      let ticket: StreamTicketResponse;
      try {
        ticket = await apiFetch<StreamTicketResponse>(
          client,
          `/api/jobs/${jobId}/stream-ticket`,
          { method: "POST", body: {} },
        );
      } catch (err) {
        if (cancelled) return;
        if (err instanceof ApiError && err.status === 401) {
          // apiFetch already cleared the session; nothing more to do.
          setConnectionState("errored");
          return;
        }
        setConnectionState("errored");
        return;
      }

      if (cancelled) return;
      const url = new URL(
        `/api/jobs/${jobId}/stream`,
        STREAM_BASE_URL || window.location.origin,
      );
      url.searchParams.set("ticket", ticket.ticket);
      const source = new EventSource(url.toString());
      sourceRef.current = source;

      source.addEventListener("open", () => {
        if (cancelled) return;
        attempts = 0;
        setConnectionState("open");
      });

      source.addEventListener("snapshot", (ev) => {
        if (cancelled) return;
        try {
          const data = JSON.parse((ev as MessageEvent).data) as RunSnapshotPayload;
          setSnapshot(data);
        } catch {
          /* ignore malformed payload — server should never produce it */
        }
      });

      const handleEvent = (ev: Event): void => {
        if (cancelled) return;
        try {
          const data = JSON.parse((ev as MessageEvent).data) as RunStreamEvent;
          setEvents((prev) => [...prev, data]);
          if (data.type === "terminal") {
            setTerminalStatus(data.status);
            setConnectionState("closed");
            source.close();
            sourceRef.current = null;
          }
        } catch {
          /* ignore */
        }
      };
      for (const eventName of [
        "state_transition",
        "job_dispatched",
        "dispatch_failed",
        "callback_received",
        "run_log_appended",
        "mode_progress",
        "terminal",
      ]) {
        source.addEventListener(eventName, handleEvent);
      }

      source.addEventListener("error", () => {
        if (cancelled) return;
        // EventSource fires `error` on both transient blips and
        // permanent failures. We close manually + retry once with a
        // fresh ticket; native auto-reconnect would re-use the
        // expired one.
        source.close();
        sourceRef.current = null;
        if (attempts < RECONNECT_ATTEMPTS) {
          attempts += 1;
          setConnectionState("connecting");
          void open();
          return;
        }
        setConnectionState("errored");
      });
    };

    void open();

    return () => {
      cancelled = true;
      sourceRef.current?.close();
      sourceRef.current = null;
    };
  }, [client, jobId, options.disabled]);

  return { connectionState, snapshot, events, terminalStatus };
}
